import { useTranslation } from "react-i18next";
import { useState, useEffect, useMemo } from "react";
import TextInput from "../../InputComponents/TextInput";
import axiosClient from "../../../api/api";
import SelectInput from "../../InputComponents/SelectInput";
import georgianCities from "../../../data/georgianCities";
import { HIGHLAND_CITIES } from "../../../data/deliveryTariffs";

import { BiSearch } from "react-icons/bi";
import { BsInfoCircle } from "react-icons/bs";
import PriceInfoButton from "../../PriceExplain/PriceInfoButton";
import { explainFromCalculator } from "../../PriceExplain/fromCalculator";
import { PRICE_INFO_THRESHOLD_KG } from "../../PriceExplain/billedWeight";

// Countries hidden from the calculator's country dropdown. Currently: Turkey
// (country_id=4) — service discontinued.
const HIDDEN_COUNTRY_IDS = new Set([4]);

// The `country` field on abroad_offices in the DB is an ops-facing string
// (e.g. "Land - China -5.5 $ - 1 kg", "აშშ — ვილმინგტონი") — mixed languages
// and inconsistent formatting. For the public calculator dropdown we build a
// clean Georgian/English label from the office's `country_id` + `type` so the
// list reads uniformly regardless of what admins typed.
const COUNTRY_KEY_BY_ID = { 3: "china", 10: "usa", 11: "greece" };
const MODE_KEY_BY_TYPE  = { 1: "land",  2: "air", 3: "sea" };
const buildOfficeLabel = (office, t) => {
   const countryKey = COUNTRY_KEY_BY_ID[Number(office.country_id)];
   const modeKey    = MODE_KEY_BY_TYPE[Number(office.type)];
   // Unknown country: fall back to whatever admins entered, minus trailing
   // pricing bits after " - " (which is what most rows have appended).
   if (!countryKey) {
      return (office.country || "").split(" - ")[0] || office.country;
   }
   const countryLabel = t(countryKey);
   // China has multiple warehouses (air/sea/land) — append the mode so they
   // don't collide. Single-warehouse countries (USA=air, Greece=land) skip
   // the suffix to keep the label short.
   const isChina = Number(office.country_id) === 3;
   return isChina && modeKey
      ? `${countryLabel} — ${t(modeKey)}`
      : countryLabel;
};

const CalculatorSection = () => {
   const { t } = useTranslation();
   const [calculatorType, setCalculatorType] = useState("volume");
   const [calculateVariables, setCalculatorVariables] = useState({
      x: 0,
      y: 0,
      z: 0,
      weight: 0,
      abroad_office_id: 0,
      zone: "tbilisi",
   });
   // Full office rows kept so we can look up home_delivery_only for the
   // selected office and toggle the zone selector accordingly.
   const [abOfficeRaw, setAbOfficeRaw] = useState([]);
   const abOffice = useMemo(
      () =>
         abOfficeRaw.map((o) => ({
            id: o.id,
            title: buildOfficeLabel(o, t),
         })),
      [abOfficeRaw, t]
   );
   const selectedOffice = useMemo(
      () =>
         abOfficeRaw.find(
            (o) => Number(o.id) === Number(calculateVariables.abroad_office_id)
         ) || null,
      [abOfficeRaw, calculateVariables.abroad_office_id]
   );
   // Zone selector appears whenever the office actually bills differently
   // per zone:
   //   - home_delivery_only offices (USA, Greece) — always
   //   - standard-pipeline offices that carry an active scheduled overlay
   //     with a Highland-specific rate (China from 2026-08-17)
   const scheduledActive = (() => {
      if (!selectedOffice?.scheduled_from) return false;
      if (selectedOffice.price_scheduled_highland == null) return false;
      const from = new Date(selectedOffice.scheduled_from);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      from.setHours(0, 0, 0, 0);
      return today >= from;
   })();
   const needsZone = !!selectedOffice?.home_delivery_only || scheduledActive;

   const [volumeResults, setVolumeResults] = useState({
      price: 0,
      volume_weight: 0,
      weight: 0,
   });
   // The full /calculate response, kept so the breakdown popup can explain the
   // quote (tier, volumetric rule, delivery line) without a second request.
   // The dimensions are kept alongside it because the response doesn't echo
   // them back and the popup needs them to say WHY volume did or didn't count.
   const [quote, setQuote] = useState(null);
   // Whether to show the highland-cities info popup.
   const [showHighlandInfo, setShowHighlandInfo] = useState(false);
   //search value
   const [searchValue, setSearchValue] = useState({
      code: "",
   });
   //searched parcel
   const [parcel, setParcel] = useState("");

   const handleCalculatorVariables = (value, key) => {
      setCalculatorVariables((prev) => ({ ...prev, [key]: value }));
   };

   const calculateVolume = () => {
      const payload = {
         x: parseFloat(calculateVariables.x),
         y: parseFloat(calculateVariables.y),
         z: parseFloat(calculateVariables.z),
         abroad_office_id: parseInt(calculateVariables.abroad_office_id),
         weight: parseFloat(calculateVariables.weight),
      };
      // Only send zone for home-delivery offices — the standard pipeline
      // ignores it and rejecting extra keys wouldn't be worth the noise.
      if (needsZone) payload.zone = calculateVariables.zone || "tbilisi";

      axiosClient
         .post("/calculate", payload)
         .then(({ data }) => {
            setVolumeResults({
               price: data.price,
               volume_weight: data.volume_weight,
               weight: data?.weight,
            });
            setQuote(
               explainFromCalculator(data, {
                  x: payload.x,
                  y: payload.y,
                  z: payload.z,
               })
            );
         })
         .catch((err) => {
            setQuote(null);
         });
   };

   const handleSearchValue = (value, inputName) => {
      setSearchValue({
         [inputName]: value,
      });
   };

   useEffect(() => {
      axiosClient.get("/abroadOffice").then(({ data }) => {
         // Hide discontinued countries. Turkey (id=4) is dropped so the user
         // never sees it in the country picker.
         setAbOfficeRaw(
            (data || []).filter(
               (o) => !HIDDEN_COUNTRY_IDS.has(Number(o.country_id))
            )
         );
      });
   }, []);

   useEffect(() => {
      if (searchValue.code !== "") {
         axiosClient.post("/searchPack", searchValue).then((res) => {
            setParcel(res.data);
         });
      }
   }, [searchValue]);

   // Human-readable list of Highland cities used in the tooltip/popup.
   const highlandLabels = georgianCities
      .filter((c) => HIGHLAND_CITIES.has(c.value))
      .map((c) => c.label);

   return (
      <div className="px-12 md:px-6 w-full flex items-center justify-center">
         <div className="w-full max-w-[80%] md:max-w-[100%] flex flex-col items-center justify-center">
            <div className="w-full flex flex-col items-center justify-center gap-4">
               <h6 className="text-black_900 text-2xl md:text-xl font-bold">
                  {t("calculator")}
               </h6>
            </div>
            <div className={"mt-12 md:mt-6 sm:w-full sm:flex sm:justify-center"}>
               <div className="flex items-center bg-[#D9D9D9] rounded-[30px] sm:w-8/12">
                  <button
                     className={`px-8 py-4 font-bold cursor-pointer rounded-[30px] transition-all sm:w-full
              sm:px-4 sm:text-xs
                     ${calculatorType === "parcel" && "bg-teal_600 text-white"
                        }`}
                     onClick={() => {
                        setCalculatorType("parcel");
                     }}
                  >
                     <h2>{t("parcels")}</h2>
                  </button>
                  <button
                     className={`px-8 py-4 font-bold cursor-pointer rounded-[30px] transition-all sm:w-full
              sm:px-4 sm:text-xs
                     ${calculatorType === "volume" && "bg-teal_600 text-white"
                        }`}
                     onClick={() => {
                        setCalculatorType("volume");
                     }}
                  >
                     <h2>{t("volume")}</h2>
                  </button>
               </div>
            </div>
            <div className={`w-full mt-12 md:mt-6 gap-5`}>
               <div className="flex md:flex-col md:gap-10">
                  <div className="w-7/12 p-5 border border-[#F0F0F0] rounded-l-[20px] md:w-full md:rounded-[20px]">
                     {calculatorType === "volume" && (
                        <>
                           <div className="grid grid-cols-2 gap-5 sm:grid-cols-1">
                              <TextInput
                                 name={t("width")}
                                 inputSetter={handleCalculatorVariables}
                                 inputName={"x"}
                                 style={2}
                              />
                              <TextInput
                                 name={t("height")}
                                 inputSetter={handleCalculatorVariables}
                                 inputName={"y"}
                                 style={2}
                              />
                              <TextInput
                                 name={t("length")}
                                 inputSetter={handleCalculatorVariables}
                                 inputName={"z"}
                                 style={2}
                              />
                              <TextInput
                                 name={t("weight")}
                                 inputSetter={handleCalculatorVariables}
                                 inputName={"weight"}
                                 style={2}
                              />

                              <SelectInput
                                 options={abOffice}
                                 name={t("country")}
                                 inputName={"abroad_office_id"}
                                 inputSetter={handleCalculatorVariables}
                                 style={2}
                              />

                              {/* Zone selector — only for home-delivery
                                  offices (USA, Greece). Everything else uses
                                  the standard per-kg pipeline where the
                                  destination doesn't affect the price. */}
                              {needsZone && (
                                 <div className="flex flex-col gap-1">
                                    <SelectInput
                                       options={[
                                          {
                                             id: "tbilisi",
                                             title: t("tbilisi") || "თბილისი",
                                          },
                                          {
                                             id: "region",
                                             title: t("region") || "რეგიონი",
                                          },
                                          {
                                             id: "highland",
                                             title:
                                                t("highland") ||
                                                "მაღალმთიანი",
                                          },
                                       ]}
                                       name={
                                          t("delivery_zone") ||
                                          "მიწოდების ზონა"
                                       }
                                       inputName={"zone"}
                                       inputSetter={handleCalculatorVariables}
                                       style={2}
                                    />
                                    <button
                                       type="button"
                                       onClick={() =>
                                          setShowHighlandInfo((v) => !v)
                                       }
                                       className="text-[11px] text-teal_600 font-semibold
                                                  flex items-center gap-1 self-start hover:underline"
                                    >
                                       <BsInfoCircle className="text-sm" />
                                       {t("what_is_highland") ||
                                          "რას ნიშნავს მაღალმთიანი?"}
                                    </button>
                                    {showHighlandInfo && (
                                       <div
                                          className="mt-1 text-[11px] text-slate-700 bg-teal-50
                                                     rounded-lg px-3 py-2 border border-teal-100
                                                     leading-snug"
                                       >
                                          <p className="font-semibold text-teal-800 mb-1">
                                             {t("highland") || "მაღალმთიანი"}:
                                          </p>
                                          <p>{highlandLabels.join(", ")}.</p>
                                          <p className="mt-1 text-slate-500">
                                             {t("other_cities_use_tbilisi_rate") ||
                                                "დანარჩენ ქალაქებზე გამოიყენება „თბილისი / რეგიონები“-ს ტარიფი."}
                                          </p>
                                       </div>
                                    )}
                                 </div>
                              )}
                           </div>
                           <div
                              className="mt-5 pt-5 border-t-2 border-[#F0F0F0] flex items-center gap-10 sm:flex-col
                  sm:gap-5 sm:items-start"
                           >
                              <button
                                 onClick={calculateVolume}
                                 className="bg-teal_600 p-3 text-white rounded-[30px] sm:order-2"
                              >
                                 {t("calculate")}
                              </button>
                              <div className="flex items-center gap-5 sm:order-1">
                                 <p className="font-semibold text-sm">
                                    {volumeResults.weight} {t("kg")}
                                 </p>{" "}
                                 <p className="font-semibold text-sm">
                                    {`${volumeResults.volume_weight} ${t("kg")} (${t("volume")})`}
                                 </p>
                                 <div className="flex items-center gap-1.5">
                                    <p className="font-semibold text-sm">
                                       {volumeResults.price} ₾
                                    </p>
                                    {/* Same threshold and same popup as the
                                        parcel cards — a visitor gets the answer
                                        a customer would. */}
                                    {quote &&
                                       Number(quote.weight.chargeable) >
                                          PRICE_INFO_THRESHOLD_KG && (
                                          <PriceInfoButton explain={quote} />
                                       )}
                                 </div>
                              </div>
                           </div>
                        </>
                     )}
                     {calculatorType === "parcel" && (
                        <div
                           className="bg-white self-start flex md:flex-1 flex-col
                            h-full items-center justify-start rounded-[34px] w-full"
                        >
                           <div className="flex flex-col items-start justify-start w-full gap-3">
                              <h2 className="text-base sm:text-sm font-bold">
                                 {t("track_parcels")}
                              </h2>
                              <div className="w-full flex flex-col gap-5">
                                 <p className="text-sm sm:text-xs">
                                    {t("track_parcels_quote")}
                                 </p>
                                 <TextInput
                                    PrefixIcon={BiSearch}
                                    placeholder={"Tracking Number"}
                                    inputSetter={handleSearchValue}
                                    inputName={"code"}
                                    style={2}
                                 />
                                 {parcel === "" ? (
                                    searchValue.code !== "" && (
                                       <p className="font-semibold text-red-500 text-sm">
                                          {t("data_not_found")}
                                       </p>
                                    )
                                 ) : (
                                    <p className="font-semibold text-teal_600 text-sm">
                                       {t("parcel_status")}
                                       {parcel.status === 1 && t("received")}
                                       {parcel.status === 2 && t("sent")}
                                       {parcel.status === 3 && t("arrived")}
                                       {parcel.status === 4 && t("takeout")}
                                    </p>
                                 )}
                              </div>
                           </div>
                        </div>
                     )}
                  </div>
                  <div className="w-5/12 bg-teal_600 p-5 text-white font-bold rounded-r-[20px] md:w-full md:rounded-[20px]">
                     <p className="text-base">{t("calculatorsTitle")}</p>
                     <p className="text-sm mt-5">{t("calculatorsTextOne")}</p>
                  </div>
               </div>
            </div>
         </div>
      </div>
   );
};

export default CalculatorSection;
